import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { LucideIcon } from "lucide-react";

import { isMobileShellViewport, MOBILE_SHELL_MQ } from "@/lib/viewport";
import type { BookingAssignedRateRailPayload } from "@/features/bookings/BookingAssignedRateRail";
import type { ScheduleResponse } from "@/types/api";
import type { RateLineRow } from "@/types/rate";
import {
  RAIL_PANEL_ORDER,
  enforceMaxExpanded,
  ensureDefaultExpandedWhenAllCollapsed,
  ensureNotesExpandedWhenCapacity,
  maxExpandedRailPanels,
  nextFocusPanel,
  railPanelFlags,
  type BookingRailSlotId,
  type RailPanelFlags,
  type RailStackPanelId,
} from "@/shell/railStackLayout";

import { RAIL_STACK_DURATION_MS } from "@/lib/railAnimation";

export const RAIL_ANIM_MS = RAIL_STACK_DURATION_MS;
export const NOTIFICATIONS_ANIM_MS = 480;

export type RightRailExpandedPanel =
  | "kai"
  | "notes"
  | "rateLine"
  | "page"
  | "opportunity"
  | "lookup"
  | "blDocument"
  | "blGroup"
  | "assignedRate"
  | "matchedSchedule"
  | "documents"
  | "insight";
export type MobileRailMode = "workspace" | "notifications";

export interface PageRailMeta {
  id: string;
  title: string;
  icon?: LucideIcon;
  badge?: number;
  /** When set with `badge`, renders e.g. "3 active" beside the title. */
  badgeSuffix?: string;
  /** When false, registering the slot does not expand it. Default true. */
  autoOpen?: boolean;
  /**
   * While this page rail is expanded, do not auto-open Notes to fill spare
   * stack capacity (keeps wizards' Guide panel prominent). Users can still open
   * Notes manually from the rail header.
   */
  suppressNotesAutoExpand?: boolean;
}

interface SectionSnapshot {
  kai: boolean;
  notes: boolean;
  page: boolean;
  opportunity: boolean;
  lookup: boolean;
  blDocument: boolean;
  bookingBlGroup: boolean;
  bookingAssignedRate: boolean;
  bookingMatchedSchedule: boolean;
  bookingInsight: boolean;
  bookingDocuments: boolean;
}

interface OpportunityCompareSnapshot {
  sections: SectionSnapshot;
  expandedPanel: RightRailExpandedPanel;
}

interface RightRailContextValue {
  kaiVisible: boolean;
  kaiPanelActive: boolean;
  kaiAnimating: boolean;
  notificationsVisible: boolean;
  notificationsPanelActive: boolean;
  notificationsAnimating: boolean;
  expandedPanel: RightRailExpandedPanel;
  kaiSectionOpen: boolean;
  notesSectionOpen: boolean;
  pageRailMeta: PageRailMeta | null;
  pageRailSectionOpen: boolean;
  pageRailTarget: HTMLElement | null;
  /** Header actions (e.g. Reset) rendered beside the page-rail expand chevron. */
  pageRailHeaderActions: ReactNode | null;
  setPageRailHeaderActions: (actions: ReactNode | null) => void;
  /** Booking-detail primary cards (Opportunities / Lookup / B/L document). */
  opportunityRailMeta: PageRailMeta | null;
  opportunitySectionOpen: boolean;
  lookupRailMeta: PageRailMeta | null;
  lookupSectionOpen: boolean;
  blDocumentRailMeta: PageRailMeta | null;
  blDocumentSectionOpen: boolean;
  bookingRailTargets: Record<BookingRailSlotId, HTMLElement | null>;
  /** Booking whose Kai insight is shown in the rail, or `null` when off-page. */
  bookingInsightSourceId: string | null;
  bookingInsightSectionOpen: boolean;
  bookingDocumentsSectionOpen: boolean;
  /** Desktop stack: BL group card for the open booking detail, if any. */
  bookingBlGroupSourceId: string | null;
  bookingBlGroupSectionOpen: boolean;
  bookingAssignedRateRail: BookingAssignedRateRailPayload | null;
  bookingAssignedRateSectionOpen: boolean;
  bookingMatchedScheduleRail: ScheduleResponse | null;
  bookingMatchedScheduleSectionOpen: boolean;
  selectedRateLine: RateLineRow | null;
  mobileRailOpen: boolean;
  mobileRailMode: MobileRailMode;
  /** Desktop fixed-height card stack (booking detail + workspace rail). */
  desktopRailStack: boolean;
  toggleKaiPanel: () => void;
  closeKai: () => void;
  /** Mount Kai and open the mobile workspace sheet (used by "Edit with Kai"). */
  openMobileKai: () => void;
  toggleNotificationsPanel: () => void;
  closeNotifications: () => void;
  openMobileRail: (mode: MobileRailMode) => void;
  toggleKaiSection: () => void;
  toggleNotesSection: () => void;
  /** Collapse Notes without opening it (used when a page wants Notes present but closed). */
  collapseNotesSection: () => void;
  togglePageRailSection: () => void;
  openPageRailSection: () => void;
  closePageRailSection: () => void;
  /** Register / open / close / toggle a booking-detail primary content card. */
  setBookingRailMeta: (id: BookingRailSlotId, meta: PageRailMeta | null) => void;
  registerBookingRailTarget: (
    id: BookingRailSlotId,
    node: HTMLElement | null,
  ) => void;
  openBookingRailSection: (id: BookingRailSlotId) => void;
  closeBookingRailSection: (id: BookingRailSlotId) => void;
  toggleBookingRailSection: (id: BookingRailSlotId) => void;
  toggleBookingInsightSection: () => void;
  toggleBookingDocumentsSection: () => void;
  setBookingBlGroupRail: (sourceId: string | null) => void;
  toggleBookingBlGroupSection: () => void;
  setBookingAssignedRateRail: (
    payload: BookingAssignedRateRailPayload | null,
  ) => void;
  toggleBookingAssignedRateSection: () => void;
  setBookingMatchedScheduleRail: (sailing: ScheduleResponse | null) => void;
  toggleBookingMatchedScheduleSection: () => void;
  /** Open documents when the booking has files; keep it collapsed when empty. */
  applyBookingDocumentsAvailability: (hasDocuments: boolean) => void;
  /** Opened bill-of-lading document on the booking page, if any. */
  selectedBookingBlDocumentId: string | null;
  /** Open / close parsed B/L details from the documents list or commercial card. */
  toggleBookingBlDocument: (id: string) => void;
  setBookingBlDocumentControls: (
    selectedId: string | null,
    onToggle: ((id: string) => void) | null,
  ) => void;
  setBookingInsightSourceId: (sourceId: string | null) => void;
  setPageRailMeta: (meta: PageRailMeta | null) => void;
  registerPageRailTarget: (node: HTMLElement | null) => void;
  openRateLineDetail: (line: RateLineRow) => void;
  closeRateLineDetail: () => void;
  setMobileRailOpen: (open: boolean) => void;
  setDesktopRailStack: (stacked: boolean) => void;
  /**
   * Desktop booking detail: collapse other stack cards so the opportunity
   * compare table can use the full rail height (restored on `false`).
   */
  setOpportunityCompareFocused: (active: boolean) => void;
}

const RightRailContext = createContext<RightRailContextValue | null>(null);

function pageMetaEqual(a: PageRailMeta | null, b: PageRailMeta | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.id === b.id &&
    a.title === b.title &&
    a.badge === b.badge &&
    a.badgeSuffix === b.badgeSuffix &&
    a.icon === b.icon &&
    a.autoOpen === b.autoOpen &&
    a.suppressNotesAutoExpand === b.suppressNotesAutoExpand
  );
}

export function RightRailProvider({ children }: { children: ReactNode }) {
  const [kaiMounted, setKaiMounted] = useState(false);
  const [kaiVisible, setKaiVisible] = useState(false);
  const [kaiAnimating, setKaiAnimating] = useState(false);
  const [notificationsMounted, setNotificationsMounted] = useState(false);
  const [notificationsVisible, setNotificationsVisible] = useState(false);
  const [notificationsAnimating, setNotificationsAnimating] = useState(false);
  const [expandedPanel, setExpandedPanel] =
    useState<RightRailExpandedPanel>("notes");
  const [kaiSectionOpen, setKaiSectionOpen] = useState(true);
  const [notesSectionOpen, setNotesSectionOpen] = useState(true);
  const [pageRailMeta, setPageRailMetaState] = useState<PageRailMeta | null>(
    null,
  );
  const [pageRailSectionOpen, setPageRailSectionOpen] = useState(false);
  const [pageRailTarget, setPageRailTarget] = useState<HTMLElement | null>(null);
  const [pageRailHeaderActions, setPageRailHeaderActionsState] =
    useState<ReactNode | null>(null);
  const setPageRailHeaderActions = useCallback((actions: ReactNode | null) => {
    // Skip identical nodes so an accidentally unstable `actions` element can't
    // feed back through `usePageRailHeaderActions` and loop (React #185). Use a
    // functional update so we compare against the latest committed value
    // without adding it to this callback's dependency list.
    setPageRailHeaderActionsState((prev) =>
      Object.is(prev, actions) ? prev : actions,
    );
  }, []);
  const [bookingInsightSourceId, setBookingInsightSourceIdState] = useState<
    string | null
  >(null);
  const [bookingInsightSectionOpen, setBookingInsightSectionOpen] =
    useState(true);
  const [bookingDocumentsSectionOpen, setBookingDocumentsSectionOpen] =
    useState(false);
  const [bookingBlGroupSourceId, setBookingBlGroupSourceIdState] = useState<
    string | null
  >(null);
  const [bookingBlGroupSectionOpen, setBookingBlGroupSectionOpen] =
    useState(false);
  const [bookingAssignedRateRail, setBookingAssignedRateRailState] =
    useState<BookingAssignedRateRailPayload | null>(null);
  const [bookingAssignedRateSectionOpen, setBookingAssignedRateSectionOpen] =
    useState(false);
  const [bookingMatchedScheduleRail, setBookingMatchedScheduleRailState] =
    useState<ScheduleResponse | null>(null);
  const [bookingMatchedScheduleSectionOpen, setBookingMatchedScheduleSectionOpen] =
    useState(false);
  // Booking-detail primary content cards (first-class stack panels). Each owns
  // a `PageRailMeta` (presence + header chrome), a portal target, and an
  // open/collapsed state, mirroring the generic `page` slot but participating in
  // the stack so they coexist with each other and the secondary booking cards.
  const [opportunityRailMeta, setOpportunityRailMetaState] =
    useState<PageRailMeta | null>(null);
  const [opportunityTarget, setOpportunityTarget] = useState<HTMLElement | null>(
    null,
  );
  const [opportunitySectionOpen, setOpportunitySectionOpen] = useState(false);
  const [lookupRailMeta, setLookupRailMetaState] = useState<PageRailMeta | null>(
    null,
  );
  const [lookupTarget, setLookupTarget] = useState<HTMLElement | null>(null);
  const [lookupSectionOpen, setLookupSectionOpen] = useState(false);
  const [blDocumentRailMeta, setBlDocumentRailMetaState] =
    useState<PageRailMeta | null>(null);
  const [blDocumentTarget, setBlDocumentTarget] = useState<HTMLElement | null>(
    null,
  );
  const [blDocumentSectionOpen, setBlDocumentSectionOpen] = useState(false);
  const [selectedRateLine, setSelectedRateLine] = useState<RateLineRow | null>(
    null,
  );
  const [mobileRailOpen, setMobileRailOpen] = useState(false);
  const [mobileRailMode, setMobileRailMode] =
    useState<MobileRailMode>("workspace");
  const [desktopRailStack, setDesktopRailStack] = useState(false);
  const preNotificationsSectionsRef = useRef<SectionSnapshot | null>(null);
  const opportunityCompareFocusRef = useRef(false);
  const preOpportunityCompareRef = useRef<OpportunityCompareSnapshot | null>(
    null,
  );
  const prePageRailSectionsRef = useRef<{
    kai: boolean;
    notes: boolean;
    bookingInsight: boolean;
    bookingDocuments: boolean;
    bookingBlGroup: boolean;
    bookingAssignedRate: boolean;
    bookingMatchedSchedule: boolean;
  } | null>(null);
  const kaiMountedRef = useRef(kaiMounted);
  const kaiSectionOpenRef = useRef(kaiSectionOpen);
  const notesSectionOpenRef = useRef(notesSectionOpen);
  /** User collapsed Notes on desktop; skip auto-open until they expand again. */
  const notesUserDismissedRef = useRef(false);
  const pageRailSectionOpenRef = useRef(pageRailSectionOpen);
  const bookingInsightSectionOpenRef = useRef(bookingInsightSectionOpen);
  const bookingDocumentsSectionOpenRef = useRef(bookingDocumentsSectionOpen);
  const bookingBlGroupSourceIdRef = useRef(bookingBlGroupSourceId);
  const bookingBlGroupSectionOpenRef = useRef(bookingBlGroupSectionOpen);
  const bookingAssignedRateRailRef = useRef(bookingAssignedRateRail);
  const bookingAssignedRateSectionOpenRef = useRef(
    bookingAssignedRateSectionOpen,
  );
  const bookingMatchedScheduleRailRef = useRef(bookingMatchedScheduleRail);
  const bookingMatchedScheduleSectionOpenRef = useRef(
    bookingMatchedScheduleSectionOpen,
  );
  const opportunityRailMetaRef = useRef(opportunityRailMeta);
  const opportunitySectionOpenRef = useRef(opportunitySectionOpen);
  const lookupRailMetaRef = useRef(lookupRailMeta);
  const lookupSectionOpenRef = useRef(lookupSectionOpen);
  const blDocumentRailMetaRef = useRef(blDocumentRailMeta);
  const blDocumentSectionOpenRef = useRef(blDocumentSectionOpen);
  const documentsManualOverrideRef = useRef(false);
  const bookingBlDocToggleRef = useRef<((id: string) => void) | null>(null);
  const [selectedBookingBlDocumentId, setSelectedBookingBlDocumentId] =
    useState<string | null>(null);
  const pageRailMetaRef = useRef(pageRailMeta);
  const bookingInsightSourceIdRef = useRef(bookingInsightSourceId);
  const selectedRateLineRef = useRef(selectedRateLine);

  useEffect(() => {
    kaiMountedRef.current = kaiMounted;
  }, [kaiMounted]);

  useEffect(() => {
    kaiSectionOpenRef.current = kaiSectionOpen;
  }, [kaiSectionOpen]);

  useEffect(() => {
    notesSectionOpenRef.current = notesSectionOpen;
  }, [notesSectionOpen]);

  useEffect(() => {
    pageRailSectionOpenRef.current = pageRailSectionOpen;
  }, [pageRailSectionOpen]);

  useEffect(() => {
    bookingInsightSectionOpenRef.current = bookingInsightSectionOpen;
  }, [bookingInsightSectionOpen]);

  useEffect(() => {
    bookingDocumentsSectionOpenRef.current = bookingDocumentsSectionOpen;
  }, [bookingDocumentsSectionOpen]);

  useEffect(() => {
    bookingBlGroupSourceIdRef.current = bookingBlGroupSourceId;
  }, [bookingBlGroupSourceId]);

  useEffect(() => {
    bookingBlGroupSectionOpenRef.current = bookingBlGroupSectionOpen;
  }, [bookingBlGroupSectionOpen]);

  useEffect(() => {
    bookingAssignedRateRailRef.current = bookingAssignedRateRail;
  }, [bookingAssignedRateRail]);

  useEffect(() => {
    bookingAssignedRateSectionOpenRef.current =
      bookingAssignedRateSectionOpen;
  }, [bookingAssignedRateSectionOpen]);

  useEffect(() => {
    bookingMatchedScheduleRailRef.current = bookingMatchedScheduleRail;
  }, [bookingMatchedScheduleRail]);

  useEffect(() => {
    bookingMatchedScheduleSectionOpenRef.current =
      bookingMatchedScheduleSectionOpen;
  }, [bookingMatchedScheduleSectionOpen]);

  useEffect(() => {
    opportunityRailMetaRef.current = opportunityRailMeta;
  }, [opportunityRailMeta]);

  useEffect(() => {
    opportunitySectionOpenRef.current = opportunitySectionOpen;
  }, [opportunitySectionOpen]);

  useEffect(() => {
    lookupRailMetaRef.current = lookupRailMeta;
  }, [lookupRailMeta]);

  useEffect(() => {
    lookupSectionOpenRef.current = lookupSectionOpen;
  }, [lookupSectionOpen]);

  useEffect(() => {
    blDocumentRailMetaRef.current = blDocumentRailMeta;
  }, [blDocumentRailMeta]);

  useEffect(() => {
    blDocumentSectionOpenRef.current = blDocumentSectionOpen;
  }, [blDocumentSectionOpen]);

  useEffect(() => {
    pageRailMetaRef.current = pageRailMeta;
  }, [pageRailMeta]);

  useEffect(() => {
    bookingInsightSourceIdRef.current = bookingInsightSourceId;
  }, [bookingInsightSourceId]);

  useEffect(() => {
    selectedRateLineRef.current = selectedRateLine;
  }, [selectedRateLine]);

  const isBookingDetailRail = useCallback(
    () => bookingInsightSourceIdRef.current != null,
    [],
  );

  /** Current open state of every stack card, read from the live refs. */
  const stackOpenFlags = useCallback(
    (): RailPanelFlags =>
      railPanelFlags({
        page: pageRailSectionOpenRef.current,
        opportunity: opportunitySectionOpenRef.current,
        lookup: lookupSectionOpenRef.current,
        blDocument: blDocumentSectionOpenRef.current,
        blGroup: bookingBlGroupSectionOpenRef.current,
        assignedRate: bookingAssignedRateSectionOpenRef.current,
        matchedSchedule: bookingMatchedScheduleSectionOpenRef.current,
        documents: bookingDocumentsSectionOpenRef.current,
        insight: bookingInsightSectionOpenRef.current,
        kai: kaiSectionOpenRef.current,
        notes: notesSectionOpenRef.current,
      }),
    [],
  );

  /** Which stack cards exist in the current rail context. */
  const stackPresentFlags = useCallback(
    (): RailPanelFlags =>
      railPanelFlags({
        page: pageRailMetaRef.current != null,
        opportunity: opportunityRailMetaRef.current != null,
        lookup: lookupRailMetaRef.current != null,
        blDocument: blDocumentRailMetaRef.current != null,
        blGroup: bookingBlGroupSourceIdRef.current != null,
        assignedRate: bookingAssignedRateRailRef.current != null,
        matchedSchedule: bookingMatchedScheduleRailRef.current != null,
        documents: bookingInsightSourceIdRef.current != null,
        insight: bookingInsightSourceIdRef.current != null,
        kai: kaiMountedRef.current,
        notes: true,
      }),
    [],
  );

  /** Pick the panel that should take focus after `closedId` collapses. */
  const refocusAfterClose = useCallback(
    (closedId: RailStackPanelId): RightRailExpandedPanel => {
      const open = stackOpenFlags();
      open[closedId] = false;
      return nextFocusPanel(open, stackPresentFlags()) ?? "notes";
    },
    [stackOpenFlags, stackPresentFlags],
  );

  const collapseWorkspaceForPage = useCallback(() => {
    if (isBookingDetailRail()) {
      setPageRailSectionOpen(true);
      setExpandedPanel("page");
      return;
    }
    setKaiSectionOpen(false);
    setBookingInsightSectionOpen(false);
    setNotesSectionOpen(false);
    if (pageRailMetaRef.current?.suppressNotesAutoExpand) {
      notesUserDismissedRef.current = true;
    }
    // When notes is open only one booking card (page vs documents) may be expanded.
    if (notesSectionOpenRef.current && bookingDocumentsSectionOpenRef.current) {
      setBookingDocumentsSectionOpen(false);
    }
    setPageRailSectionOpen(true);
    setExpandedPanel("page");
  }, [isBookingDetailRail]);

  const restoreAfterPageRail = useCallback(() => {
    const previous = prePageRailSectionsRef.current;
    if (previous) {
      setKaiSectionOpen(previous.kai);
      setNotesSectionOpen(previous.notes);
      setBookingInsightSectionOpen(previous.bookingInsight);
      setBookingDocumentsSectionOpen(previous.bookingDocuments);
      setBookingBlGroupSectionOpen(previous.bookingBlGroup);
      setBookingAssignedRateSectionOpen(previous.bookingAssignedRate);
      setBookingMatchedScheduleSectionOpen(previous.bookingMatchedSchedule);
      if (previous.kai) {
        setExpandedPanel("kai");
      } else if (previous.notes) {
        setExpandedPanel("notes");
      }
      if (previous.notes) {
        notesUserDismissedRef.current = false;
      }
      prePageRailSectionsRef.current = null;
    }
    setPageRailSectionOpen(false);
  }, []);

  const setPageRailMeta = useCallback(
    (meta: PageRailMeta | null) => {
      const previous = pageRailMetaRef.current;
      const had = previous != null;
      const has = meta != null;

      if (pageMetaEqual(previous, meta)) return;

      pageRailMetaRef.current = meta;
      setPageRailMetaState(meta);

      if (!had && has) {
        if (meta.autoOpen === false) return;
        if (!bookingInsightSourceIdRef.current) {
          prePageRailSectionsRef.current = {
            kai: kaiSectionOpenRef.current,
            notes: notesSectionOpenRef.current,
            bookingInsight: bookingInsightSectionOpenRef.current,
            bookingDocuments: bookingDocumentsSectionOpenRef.current,
            bookingBlGroup: bookingBlGroupSectionOpenRef.current,
            bookingAssignedRate: bookingAssignedRateSectionOpenRef.current,
            bookingMatchedSchedule:
              bookingMatchedScheduleSectionOpenRef.current,
          };
        }
        collapseWorkspaceForPage();
        return;
      }

      if (had && !has) {
        setPageRailHeaderActionsState(null);
        restoreAfterPageRail();
      }
    },
    [collapseWorkspaceForPage, restoreAfterPageRail],
  );

  const registerPageRailTarget = useCallback((node: HTMLElement | null) => {
    setPageRailTarget((current) => (current === node ? current : node));
  }, []);

  // ── Booking-detail primary content cards ──────────────────────────────────
  // These share one generic shape (meta + target + open) across three slots.
  const bookingSlotMetaRef = useCallback((id: BookingRailSlotId) => {
    if (id === "opportunity") return opportunityRailMetaRef;
    if (id === "lookup") return lookupRailMetaRef;
    return blDocumentRailMetaRef;
  }, []);

  const bookingSlotOpenRef = useCallback((id: BookingRailSlotId) => {
    if (id === "opportunity") return opportunitySectionOpenRef;
    if (id === "lookup") return lookupSectionOpenRef;
    return blDocumentSectionOpenRef;
  }, []);

  const bookingSlotSetMeta = useCallback((id: BookingRailSlotId) => {
    if (id === "opportunity") return setOpportunityRailMetaState;
    if (id === "lookup") return setLookupRailMetaState;
    return setBlDocumentRailMetaState;
  }, []);

  const bookingSlotSetOpen = useCallback((id: BookingRailSlotId) => {
    if (id === "opportunity") return setOpportunitySectionOpen;
    if (id === "lookup") return setLookupSectionOpen;
    return setBlDocumentSectionOpen;
  }, []);

  const setBookingRailMeta = useCallback(
    (id: BookingRailSlotId, meta: PageRailMeta | null) => {
      const metaRef = bookingSlotMetaRef(id);
      if (pageMetaEqual(metaRef.current, meta)) return;
      const had = metaRef.current != null;
      metaRef.current = meta;
      bookingSlotSetMeta(id)(meta);
      // When a card is removed, collapse its body and move focus elsewhere.
      if (had && meta == null) {
        bookingSlotSetOpen(id)(false);
        setExpandedPanel((current) =>
          current === id ? refocusAfterClose(id) : current,
        );
      }
    },
    [bookingSlotMetaRef, bookingSlotSetMeta, bookingSlotSetOpen, refocusAfterClose],
  );

  const registerBookingRailTarget = useCallback(
    (id: BookingRailSlotId, node: HTMLElement | null) => {
      const setTarget =
        id === "opportunity"
          ? setOpportunityTarget
          : id === "lookup"
            ? setLookupTarget
            : setBlDocumentTarget;
      setTarget((current) => (current === node ? current : node));
    },
    [],
  );

  const openBookingRailSection = useCallback(
    (id: BookingRailSlotId) => {
      if (!bookingSlotMetaRef(id).current) return;
      if (bookingSlotOpenRef(id).current) {
        setExpandedPanel(id);
        return;
      }
      bookingSlotSetOpen(id)(true);
      setExpandedPanel(id);
    },
    [bookingSlotMetaRef, bookingSlotOpenRef, bookingSlotSetOpen],
  );

  const closeBookingRailSection = useCallback(
    (id: BookingRailSlotId) => {
      if (!bookingSlotOpenRef(id).current) return;
      bookingSlotSetOpen(id)(false);
      setExpandedPanel((current) =>
        current === id ? refocusAfterClose(id) : current,
      );
    },
    [bookingSlotOpenRef, bookingSlotSetOpen, refocusAfterClose],
  );

  const toggleBookingRailSection = useCallback(
    (id: BookingRailSlotId) => {
      if (!bookingSlotMetaRef(id).current) return;
      const open = bookingSlotOpenRef(id).current;
      // Header click on an already-open, unfocused card just focuses it.
      if (!isMobileShellViewport() && open && expandedPanel !== id) {
        setExpandedPanel(id);
        return;
      }
      if (open) {
        bookingSlotSetOpen(id)(false);
        setExpandedPanel(refocusAfterClose(id));
        return;
      }
      bookingSlotSetOpen(id)(true);
      setExpandedPanel(id);
    },
    [
      bookingSlotMetaRef,
      bookingSlotOpenRef,
      bookingSlotSetOpen,
      expandedPanel,
      refocusAfterClose,
    ],
  );

  const openKai = useCallback(() => {
    if (isMobileShellViewport()) return;
    setKaiMounted(true);
    setKaiAnimating(true);
    setKaiSectionOpen(true);
    setExpandedPanel("kai");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setKaiVisible(true));
    });
  }, []);

  const closeKai = useCallback(() => {
    setKaiVisible(false);
    setKaiAnimating(true);
    window.setTimeout(() => {
      setKaiMounted(false);
      setKaiAnimating(false);
      setKaiSectionOpen(true);
      if (pageRailMetaRef.current) {
        setPageRailSectionOpen(true);
        setExpandedPanel("page");
      } else {
        setExpandedPanel("notes");
      }
    }, RAIL_ANIM_MS);
  }, []);

  // Mobile can't dock the Kai rail, so `openKai` no-ops there. This mounts Kai
  // directly and surfaces it in the workspace sheet instead — letting flows like
  // "Edit with Kai" stay on the current page (e.g. `/dashboard`) rather than
  // navigating to the full-screen Kai route.
  const openMobileKai = useCallback(() => {
    setKaiMounted(true);
    setKaiVisible(true);
    setKaiAnimating(false);
    setKaiSectionOpen(true);
    setExpandedPanel("kai");
    setMobileRailMode("workspace");
    setMobileRailOpen(true);
  }, []);

  const openNotifications = useCallback(() => {
    preNotificationsSectionsRef.current = {
      kai: kaiSectionOpenRef.current,
      notes: notesSectionOpenRef.current,
      page: pageRailSectionOpenRef.current,
      opportunity: opportunitySectionOpenRef.current,
      lookup: lookupSectionOpenRef.current,
      blDocument: blDocumentSectionOpenRef.current,
      bookingBlGroup: bookingBlGroupSectionOpenRef.current,
      bookingAssignedRate: bookingAssignedRateSectionOpenRef.current,
      bookingMatchedSchedule: bookingMatchedScheduleSectionOpenRef.current,
      bookingInsight: bookingInsightSectionOpenRef.current,
      bookingDocuments: bookingDocumentsSectionOpenRef.current,
    };
    setKaiSectionOpen(false);
    setNotesSectionOpen(false);
    setPageRailSectionOpen(false);
    setOpportunitySectionOpen(false);
    setLookupSectionOpen(false);
    setBlDocumentSectionOpen(false);
    setBookingBlGroupSectionOpen(false);
    setBookingAssignedRateSectionOpen(false);
    setBookingMatchedScheduleSectionOpen(false);
    setBookingInsightSectionOpen(false);
    setBookingDocumentsSectionOpen(false);
    setNotificationsMounted(true);
    setNotificationsAnimating(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setNotificationsVisible(true));
    });
  }, []);

  const captureSectionSnapshot = useCallback((): SectionSnapshot => {
    return {
      kai: kaiSectionOpenRef.current,
      notes: notesSectionOpenRef.current,
      page: pageRailSectionOpenRef.current,
      opportunity: opportunitySectionOpenRef.current,
      lookup: lookupSectionOpenRef.current,
      blDocument: blDocumentSectionOpenRef.current,
      bookingBlGroup: bookingBlGroupSectionOpenRef.current,
      bookingAssignedRate: bookingAssignedRateSectionOpenRef.current,
      bookingMatchedSchedule: bookingMatchedScheduleSectionOpenRef.current,
      bookingInsight: bookingInsightSectionOpenRef.current,
      bookingDocuments: bookingDocumentsSectionOpenRef.current,
    };
  }, []);

  const restoreExpandedFromSnapshot = useCallback(
    (previous: SectionSnapshot, panel: RightRailExpandedPanel) => {
      if (panel === "opportunity" && previous.opportunity) {
        setExpandedPanel("opportunity");
        return;
      }
      if (panel === "lookup" && previous.lookup) {
        setExpandedPanel("lookup");
        return;
      }
      if (panel === "blDocument" && previous.blDocument) {
        setExpandedPanel("blDocument");
        return;
      }
      if (panel === "blGroup" && previous.bookingBlGroup) {
        setExpandedPanel("blGroup");
        return;
      }
      if (panel === "assignedRate" && previous.bookingAssignedRate) {
        setExpandedPanel("assignedRate");
        return;
      }
      if (panel === "matchedSchedule" && previous.bookingMatchedSchedule) {
        setExpandedPanel("matchedSchedule");
        return;
      }
      if (panel === "documents" && previous.bookingDocuments) {
        setExpandedPanel("documents");
        return;
      }
      if (panel === "insight" && previous.bookingInsight) {
        setExpandedPanel("insight");
        return;
      }
      if (panel === "page" && previous.page && pageRailMetaRef.current) {
        setExpandedPanel("page");
        return;
      }
      if (panel === "kai" && previous.kai) {
        setExpandedPanel("kai");
        return;
      }
      if (previous.opportunity) {
        setExpandedPanel("opportunity");
      } else if (previous.page && pageRailMetaRef.current) {
        setExpandedPanel("page");
      } else if (previous.kai) {
        setExpandedPanel("kai");
      } else if (previous.notes) {
        setExpandedPanel("notes");
      }
    },
    [],
  );

  const setOpportunityCompareFocused = useCallback(
    (active: boolean) => {
      if (isMobileShellViewport()) return;
      if (active) {
        if (opportunityCompareFocusRef.current) return;
        opportunityCompareFocusRef.current = true;
        preOpportunityCompareRef.current = {
          sections: captureSectionSnapshot(),
          expandedPanel,
        };
        setPageRailSectionOpen(false);
        setLookupSectionOpen(false);
        setBlDocumentSectionOpen(false);
        setBookingBlGroupSectionOpen(false);
        setBookingAssignedRateSectionOpen(false);
        setBookingMatchedScheduleSectionOpen(false);
        setBookingInsightSectionOpen(false);
        setBookingDocumentsSectionOpen(false);
        setKaiSectionOpen(false);
        setNotesSectionOpen(false);
        setOpportunitySectionOpen(true);
        setExpandedPanel("opportunity");
        return;
      }
      if (!opportunityCompareFocusRef.current) return;
      opportunityCompareFocusRef.current = false;
      const previous = preOpportunityCompareRef.current;
      preOpportunityCompareRef.current = null;
      if (!previous) return;
      const { sections } = previous;
      setKaiSectionOpen(sections.kai);
      setNotesSectionOpen(sections.notes);
      setPageRailSectionOpen(sections.page);
      setOpportunitySectionOpen(sections.opportunity);
      setLookupSectionOpen(sections.lookup);
      setBlDocumentSectionOpen(sections.blDocument);
      setBookingBlGroupSectionOpen(sections.bookingBlGroup);
      setBookingAssignedRateSectionOpen(sections.bookingAssignedRate);
      setBookingMatchedScheduleSectionOpen(sections.bookingMatchedSchedule);
      setBookingInsightSectionOpen(sections.bookingInsight);
      setBookingDocumentsSectionOpen(sections.bookingDocuments);
      restoreExpandedFromSnapshot(sections, previous.expandedPanel);
    },
    [captureSectionSnapshot, expandedPanel, restoreExpandedFromSnapshot],
  );

  const closeNotifications = useCallback(() => {
    setNotificationsVisible(false);
    setNotificationsAnimating(true);
    window.setTimeout(() => {
      setNotificationsMounted(false);
      setNotificationsAnimating(false);
      const previous = preNotificationsSectionsRef.current;
      if (previous) {
        setKaiSectionOpen(previous.kai);
        setNotesSectionOpen(previous.notes);
        setPageRailSectionOpen(previous.page);
        setOpportunitySectionOpen(previous.opportunity);
        setLookupSectionOpen(previous.lookup);
        setBlDocumentSectionOpen(previous.blDocument);
        setBookingInsightSectionOpen(previous.bookingInsight);
        setBookingDocumentsSectionOpen(previous.bookingDocuments);
        setBookingBlGroupSectionOpen(previous.bookingBlGroup);
        setBookingAssignedRateSectionOpen(previous.bookingAssignedRate);
        setBookingMatchedScheduleSectionOpen(previous.bookingMatchedSchedule);
        if (previous.page && pageRailMetaRef.current) {
          setExpandedPanel("page");
        } else if (previous.kai) {
          setExpandedPanel("kai");
        } else if (previous.notes) {
          setExpandedPanel("notes");
        }
        preNotificationsSectionsRef.current = null;
      }
    }, NOTIFICATIONS_ANIM_MS);
  }, []);

  const toggleKaiPanel = useCallback(() => {
    if (isMobileShellViewport()) return;
    if (kaiMounted) {
      if (kaiVisible) closeKai();
      return;
    }
    openKai();
  }, [closeKai, kaiMounted, kaiVisible, openKai]);

  const toggleNotificationsPanel = useCallback(() => {
    if (notificationsMounted) {
      if (notificationsVisible) closeNotifications();
      else openNotifications();
      return;
    }
    openNotifications();
  }, [
    closeNotifications,
    notificationsMounted,
    notificationsVisible,
    openNotifications,
  ]);

  const openMobileRail = useCallback(
    (mode: MobileRailMode) => {
      setMobileRailMode(mode);
      if (mode === "notifications") {
        if (!notificationsMounted || !notificationsVisible) {
          openNotifications();
        }
      }
      setMobileRailOpen(true);
    },
    [notificationsMounted, notificationsVisible, openNotifications],
  );

  const toggleKaiSection = useCallback(() => {
    if (isMobileShellViewport()) return;
    if (!kaiMounted) {
      openKai();
      return;
    }
    // Header click on an already-open, unfocused card just focuses it.
    if (kaiSectionOpenRef.current && expandedPanel !== "kai") {
      setExpandedPanel("kai");
      return;
    }
    if (kaiSectionOpenRef.current) {
      setKaiSectionOpen(false);
      setExpandedPanel(refocusAfterClose("kai"));
      return;
    }
    setKaiSectionOpen(true);
    setExpandedPanel("kai");
  }, [expandedPanel, kaiMounted, openKai, refocusAfterClose]);

  const toggleNotesSection = useCallback(() => {
    if (
      !isMobileShellViewport() &&
      notesSectionOpenRef.current &&
      expandedPanel !== "notes"
    ) {
      setExpandedPanel("notes");
      return;
    }
    if (notesSectionOpenRef.current) {
      notesUserDismissedRef.current = true;
      setNotesSectionOpen(false);
      setExpandedPanel(refocusAfterClose("notes"));
      return;
    }
    notesUserDismissedRef.current = false;
    setNotesSectionOpen(true);
    setExpandedPanel("notes");
  }, [expandedPanel, refocusAfterClose]);

  const collapseNotesSection = useCallback(() => {
    if (!notesSectionOpenRef.current) return;
    notesUserDismissedRef.current = true;
    setNotesSectionOpen(false);
    setExpandedPanel(refocusAfterClose("notes"));
  }, [refocusAfterClose]);

  const togglePageRailSection = useCallback(() => {
    if (!pageRailMetaRef.current) return;
    if (
      !isMobileShellViewport() &&
      pageRailSectionOpenRef.current &&
      expandedPanel !== "page"
    ) {
      setExpandedPanel("page");
      return;
    }
    if (pageRailSectionOpenRef.current) {
      setPageRailSectionOpen(false);
      setExpandedPanel(refocusAfterClose("page"));
      return;
    }
    setPageRailSectionOpen(true);
    setExpandedPanel("page");
  }, [expandedPanel, refocusAfterClose]);

  const openPageRailSection = useCallback(() => {
    if (!pageRailMetaRef.current) return;
    if (pageRailSectionOpenRef.current) {
      setExpandedPanel("page");
      return;
    }
    collapseWorkspaceForPage();
  }, [collapseWorkspaceForPage]);

  const closePageRailSection = useCallback(() => {
    if (!pageRailMetaRef.current) return;
    if (!pageRailSectionOpenRef.current) return;
    setPageRailSectionOpen(false);
    if (kaiMounted && kaiSectionOpenRef.current) {
      setExpandedPanel("kai");
    } else if (notesSectionOpenRef.current) {
      setExpandedPanel("notes");
    }
  }, [kaiMounted]);

  const toggleBookingInsightSection = useCallback(() => {
    if (
      !isMobileShellViewport() &&
      bookingInsightSectionOpenRef.current &&
      expandedPanel !== "insight"
    ) {
      setExpandedPanel("insight");
      return;
    }
    if (bookingInsightSectionOpenRef.current) {
      setBookingInsightSectionOpen(false);
      setExpandedPanel(refocusAfterClose("insight"));
      return;
    }
    setBookingInsightSectionOpen(true);
    setExpandedPanel("insight");
  }, [expandedPanel, refocusAfterClose]);

  const toggleBookingDocumentsSection = useCallback(() => {
    documentsManualOverrideRef.current = true;
    if (
      !isMobileShellViewport() &&
      bookingDocumentsSectionOpenRef.current &&
      expandedPanel !== "documents"
    ) {
      setExpandedPanel("documents");
      return;
    }
    if (bookingDocumentsSectionOpenRef.current) {
      setBookingDocumentsSectionOpen(false);
      setExpandedPanel(refocusAfterClose("documents"));
      return;
    }
    setBookingDocumentsSectionOpen(true);
    setExpandedPanel("documents");
  }, [expandedPanel, refocusAfterClose]);

  const applyBookingDocumentsAvailability = useCallback(
    (hasDocuments: boolean) => {
      if (documentsManualOverrideRef.current) return;
      setBookingDocumentsSectionOpen(hasDocuments);
      if (hasDocuments) return;
      setExpandedPanel((current) => {
        if (current !== "documents") return current;
        if (pageRailSectionOpenRef.current) return "page";
        if (opportunitySectionOpenRef.current) return "opportunity";
        if (lookupSectionOpenRef.current) return "lookup";
        if (blDocumentSectionOpenRef.current) return "blDocument";
        if (bookingBlGroupSectionOpenRef.current) return "blGroup";
        if (bookingAssignedRateSectionOpenRef.current) return "assignedRate";
        if (bookingMatchedScheduleSectionOpenRef.current) {
          return "matchedSchedule";
        }
        if (bookingInsightSectionOpenRef.current) return "insight";
        if (kaiSectionOpenRef.current) return "kai";
        if (notesSectionOpenRef.current) return "notes";
        return current;
      });
    },
    [],
  );

  const setBookingBlGroupRail = useCallback((sourceId: string | null) => {
    if (sourceId === bookingBlGroupSourceIdRef.current) {
      if (sourceId != null) {
        setBookingBlGroupSectionOpen(true);
        setExpandedPanel("blGroup");
      }
      return;
    }
    bookingBlGroupSourceIdRef.current = sourceId;
    setBookingBlGroupSourceIdState(sourceId);
    if (sourceId) {
      setBookingBlGroupSectionOpen(true);
      setExpandedPanel("blGroup");
    } else {
      setBookingBlGroupSectionOpen(false);
      setExpandedPanel((current) =>
        current === "blGroup" ? refocusAfterClose("blGroup") : current,
      );
    }
  }, [refocusAfterClose]);

  const toggleBookingBlGroupSection = useCallback(() => {
    if (!bookingBlGroupSourceIdRef.current) return;
    if (
      !isMobileShellViewport() &&
      bookingBlGroupSectionOpenRef.current &&
      expandedPanel !== "blGroup"
    ) {
      setExpandedPanel("blGroup");
      return;
    }
    if (bookingBlGroupSectionOpenRef.current) {
      setBookingBlGroupSectionOpen(false);
      setExpandedPanel(refocusAfterClose("blGroup"));
      return;
    }
    setBookingBlGroupSectionOpen(true);
    setExpandedPanel("blGroup");
  }, [expandedPanel, refocusAfterClose]);

  const setBookingAssignedRateRail = useCallback(
    (payload: BookingAssignedRateRailPayload | null) => {
      const prev = bookingAssignedRateRailRef.current;
      const same =
        prev?.sourceId === payload?.sourceId &&
        prev?.contractRef === payload?.contractRef &&
        prev?.rateCardId === payload?.rateCardId;
      if (same) {
        if (payload != null) {
          setBookingAssignedRateSectionOpen(true);
          setExpandedPanel("assignedRate");
        }
        return;
      }
      bookingAssignedRateRailRef.current = payload;
      setBookingAssignedRateRailState(payload);
      if (payload) {
        setBookingAssignedRateSectionOpen(true);
        setExpandedPanel("assignedRate");
      } else {
        setBookingAssignedRateSectionOpen(false);
        setExpandedPanel((current) =>
          current === "assignedRate"
            ? refocusAfterClose("assignedRate")
            : current,
        );
      }
    },
    [refocusAfterClose],
  );

  const toggleBookingAssignedRateSection = useCallback(() => {
    if (!bookingAssignedRateRailRef.current) return;
    if (
      !isMobileShellViewport() &&
      bookingAssignedRateSectionOpenRef.current &&
      expandedPanel !== "assignedRate"
    ) {
      setExpandedPanel("assignedRate");
      return;
    }
    if (bookingAssignedRateSectionOpenRef.current) {
      setBookingAssignedRateSectionOpen(false);
      setExpandedPanel(refocusAfterClose("assignedRate"));
      return;
    }
    setBookingAssignedRateSectionOpen(true);
    setExpandedPanel("assignedRate");
  }, [expandedPanel, refocusAfterClose]);

  const setBookingMatchedScheduleRail = useCallback(
    (sailing: ScheduleResponse | null) => {
      const prev = bookingMatchedScheduleRailRef.current;
      const same = prev?.id === sailing?.id;
      if (same) {
        if (sailing != null) {
          setBookingMatchedScheduleSectionOpen(true);
          setExpandedPanel("matchedSchedule");
        }
        return;
      }
      bookingMatchedScheduleRailRef.current = sailing;
      setBookingMatchedScheduleRailState(sailing);
      if (sailing) {
        setBookingMatchedScheduleSectionOpen(true);
        setExpandedPanel("matchedSchedule");
      } else {
        setBookingMatchedScheduleSectionOpen(false);
        setExpandedPanel((current) =>
          current === "matchedSchedule"
            ? refocusAfterClose("matchedSchedule")
            : current,
        );
      }
    },
    [refocusAfterClose],
  );

  const toggleBookingMatchedScheduleSection = useCallback(() => {
    if (!bookingMatchedScheduleRailRef.current) return;
    if (
      !isMobileShellViewport() &&
      bookingMatchedScheduleSectionOpenRef.current &&
      expandedPanel !== "matchedSchedule"
    ) {
      setExpandedPanel("matchedSchedule");
      return;
    }
    if (bookingMatchedScheduleSectionOpenRef.current) {
      setBookingMatchedScheduleSectionOpen(false);
      setExpandedPanel(refocusAfterClose("matchedSchedule"));
      return;
    }
    setBookingMatchedScheduleSectionOpen(true);
    setExpandedPanel("matchedSchedule");
  }, [expandedPanel, refocusAfterClose]);

  const setBookingBlDocumentControls = useCallback(
    (selectedId: string | null, onToggle: ((id: string) => void) | null) => {
      bookingBlDocToggleRef.current = onToggle;
      setSelectedBookingBlDocumentId(selectedId);
    },
    [],
  );

  const toggleBookingBlDocument = useCallback((id: string) => {
    bookingBlDocToggleRef.current?.(id);
  }, []);

  const setBookingInsightSourceId = useCallback((sourceId: string | null) => {
    if (bookingInsightSourceIdRef.current === sourceId) return;
    bookingInsightSourceIdRef.current = sourceId;
    setBookingInsightSourceIdState(sourceId);
    documentsManualOverrideRef.current = false;
    if (sourceId) {
      setBookingInsightSectionOpen(true);
      // Stay collapsed until documents load; expand only when files exist.
      setBookingDocumentsSectionOpen(false);
      // Leave room for Opportunities on booking detail.
      setNotesSectionOpen(false);
      if (pageRailMetaRef.current) {
        setExpandedPanel("page");
      } else {
        setExpandedPanel("insight");
      }
      return;
    }
    bookingBlGroupSourceIdRef.current = null;
    setBookingBlGroupSourceIdState(null);
    setBookingBlGroupSectionOpen(false);
    bookingAssignedRateRailRef.current = null;
    setBookingAssignedRateRailState(null);
    setBookingAssignedRateSectionOpen(false);
    bookingMatchedScheduleRailRef.current = null;
    setBookingMatchedScheduleRailState(null);
    setBookingMatchedScheduleSectionOpen(false);
    opportunityRailMetaRef.current = null;
    setOpportunityRailMetaState(null);
    setOpportunitySectionOpen(false);
    lookupRailMetaRef.current = null;
    setLookupRailMetaState(null);
    setLookupSectionOpen(false);
    blDocumentRailMetaRef.current = null;
    setBlDocumentRailMetaState(null);
    setBlDocumentSectionOpen(false);
  }, []);

  const closeRateLineDetail = useCallback(() => {
    setSelectedRateLine(null);
    setExpandedPanel((current) => {
      if (current !== "rateLine") return current;
      if (pageRailMetaRef.current && pageRailSectionOpenRef.current) {
        return "page";
      }
      if (opportunitySectionOpenRef.current) return "opportunity";
      if (lookupSectionOpenRef.current) return "lookup";
      if (blDocumentSectionOpenRef.current) return "blDocument";
      if (bookingBlGroupSectionOpenRef.current) return "blGroup";
      if (bookingAssignedRateSectionOpenRef.current) return "assignedRate";
      if (bookingMatchedScheduleSectionOpenRef.current) {
        return "matchedSchedule";
      }
      if (bookingDocumentsSectionOpenRef.current) return "documents";
      if (bookingInsightSectionOpenRef.current) return "insight";
      if (kaiMounted && kaiSectionOpenRef.current) return "kai";
      return "notes";
    });
  }, [kaiMounted]);

  const openRateLineDetail = useCallback(
    (line: RateLineRow) => {
      if (selectedRateLineRef.current?.id === line.id) {
        closeRateLineDetail();
        return;
      }

      setExpandedPanel("rateLine");
      setSelectedRateLine(line);

      if (isMobileShellViewport()) {
        setMobileRailMode("workspace");
        setMobileRailOpen(true);
      }
    },
    [closeRateLineDetail],
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_SHELL_MQ);
    const onChange = () => {
      if (mq.matches && kaiMounted) closeKai();
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [closeKai, kaiMounted]);

  useEffect(() => {
    if (!kaiAnimating) return;
    if (kaiVisible) {
      const id = window.setTimeout(() => setKaiAnimating(false), RAIL_ANIM_MS);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [kaiAnimating, kaiVisible]);

  useEffect(() => {
    if (!notificationsAnimating) return;
    if (notificationsVisible) {
      const id = window.setTimeout(
        () => setNotificationsAnimating(false),
        NOTIFICATIONS_ANIM_MS,
      );
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [notificationsAnimating, notificationsVisible]);

  // Cap parallel expanded stack cards on desktop: all may open when ≤3 cards are
  // on the rail; otherwise at most two, collapsing from the bottom first.
  useEffect(() => {
    if (isMobileShellViewport()) return;
    if (opportunityCompareFocusRef.current) return;
    const present = railPanelFlags({
      page: pageRailMeta != null,
      opportunity: opportunityRailMeta != null,
      lookup: lookupRailMeta != null,
      blDocument: blDocumentRailMeta != null,
      blGroup: bookingBlGroupSourceId != null,
      assignedRate: bookingAssignedRateRail != null,
      matchedSchedule: bookingMatchedScheduleRail != null,
      documents: false,
      insight: bookingInsightSourceId != null,
      kai: kaiMounted,
      notes: true,
    });
    const open = railPanelFlags({
      page: pageRailSectionOpen,
      opportunity: opportunitySectionOpen,
      lookup: lookupSectionOpen,
      blDocument: blDocumentSectionOpen,
      blGroup: bookingBlGroupSectionOpen,
      assignedRate: bookingAssignedRateSectionOpen,
      matchedSchedule: bookingMatchedScheduleSectionOpen,
      documents: bookingDocumentsSectionOpen,
      insight: bookingInsightSectionOpen,
      kai: kaiSectionOpen,
      notes: notesSectionOpen,
    });
    const protectedId = (RAIL_PANEL_ORDER as readonly string[]).includes(
      expandedPanel,
    )
      ? (expandedPanel as RailStackPanelId)
      : null;
    const capped = enforceMaxExpanded(
      open,
      present,
      protectedId,
      maxExpandedRailPanels(present),
    );
    const maxExpanded = maxExpandedRailPanels(present);
    const suppressNotesAutoExpand =
      pageRailMeta?.suppressNotesAutoExpand === true;
    const skipNotesDefaultExpand =
      notesUserDismissedRef.current || suppressNotesAutoExpand;
    const withNotesDefault = skipNotesDefaultExpand
      ? capped
      : ensureDefaultExpandedWhenAllCollapsed(capped, present);
    const normalized = ensureNotesExpandedWhenCapacity(
      withNotesDefault,
      present,
      maxExpanded,
      skipNotesDefaultExpand,
    );
    if (normalized.notes && !withNotesDefault.notes) {
      notesUserDismissedRef.current = false;
    }
    if (normalized.page !== open.page) setPageRailSectionOpen(normalized.page);
    if (normalized.opportunity !== open.opportunity) {
      setOpportunitySectionOpen(normalized.opportunity);
    }
    if (normalized.lookup !== open.lookup) {
      setLookupSectionOpen(normalized.lookup);
    }
    if (normalized.blDocument !== open.blDocument) {
      setBlDocumentSectionOpen(normalized.blDocument);
    }
    if (normalized.blGroup !== open.blGroup) {
      setBookingBlGroupSectionOpen(normalized.blGroup);
    }
    if (normalized.assignedRate !== open.assignedRate) {
      setBookingAssignedRateSectionOpen(normalized.assignedRate);
    }
    if (normalized.matchedSchedule !== open.matchedSchedule) {
      setBookingMatchedScheduleSectionOpen(normalized.matchedSchedule);
    }
    if (normalized.documents !== open.documents) {
      setBookingDocumentsSectionOpen(normalized.documents);
    }
    if (normalized.insight !== open.insight) {
      setBookingInsightSectionOpen(normalized.insight);
    }
    if (normalized.kai !== open.kai) setKaiSectionOpen(normalized.kai);
    if (normalized.notes !== open.notes) {
      setNotesSectionOpen(normalized.notes);
      if (normalized.notes) setExpandedPanel("notes");
    }
  }, [
    pageRailMeta,
    opportunityRailMeta,
    lookupRailMeta,
    blDocumentRailMeta,
    bookingBlGroupSourceId,
    bookingAssignedRateRail,
    bookingMatchedScheduleRail,
    bookingInsightSourceId,
    kaiMounted,
    pageRailSectionOpen,
    opportunitySectionOpen,
    lookupSectionOpen,
    blDocumentSectionOpen,
    bookingBlGroupSectionOpen,
    bookingAssignedRateSectionOpen,
    bookingMatchedScheduleSectionOpen,
    bookingDocumentsSectionOpen,
    bookingInsightSectionOpen,
    kaiSectionOpen,
    notesSectionOpen,
    expandedPanel,
  ]);

  const value = useMemo<RightRailContextValue>(
    () => ({
      kaiVisible: kaiMounted && kaiVisible,
      kaiPanelActive: kaiMounted,
      kaiAnimating,
      notificationsVisible: notificationsMounted && notificationsVisible,
      notificationsPanelActive: notificationsMounted,
      notificationsAnimating,
      expandedPanel,
      kaiSectionOpen,
      notesSectionOpen,
      pageRailMeta,
      pageRailSectionOpen,
      pageRailTarget,
      pageRailHeaderActions,
      setPageRailHeaderActions,
      opportunityRailMeta,
      opportunitySectionOpen,
      lookupRailMeta,
      lookupSectionOpen,
      blDocumentRailMeta,
      blDocumentSectionOpen,
      bookingInsightSourceId,
      bookingInsightSectionOpen,
      bookingDocumentsSectionOpen,
      bookingBlGroupSourceId,
      bookingBlGroupSectionOpen,
      bookingAssignedRateRail,
      bookingAssignedRateSectionOpen,
      bookingMatchedScheduleRail,
      bookingMatchedScheduleSectionOpen,
      selectedRateLine,
      mobileRailOpen,
      mobileRailMode,
      desktopRailStack,
      toggleKaiPanel,
      closeKai,
      openMobileKai,
      toggleNotificationsPanel,
      closeNotifications,
      openMobileRail,
      toggleKaiSection,
      toggleNotesSection,
      collapseNotesSection,
      togglePageRailSection,
      openPageRailSection,
      closePageRailSection,
      toggleBookingInsightSection,
      toggleBookingDocumentsSection,
      setBookingBlGroupRail,
      toggleBookingBlGroupSection,
      setBookingAssignedRateRail,
      toggleBookingAssignedRateSection,
      setBookingMatchedScheduleRail,
      toggleBookingMatchedScheduleSection,
      applyBookingDocumentsAvailability,
      selectedBookingBlDocumentId,
      toggleBookingBlDocument,
      setBookingBlDocumentControls,
      setBookingInsightSourceId,
      setPageRailMeta,
      registerPageRailTarget,
      openRateLineDetail,
      closeRateLineDetail,
      setMobileRailOpen,
      setDesktopRailStack,
      setOpportunityCompareFocused,
      bookingRailTargets: {
        opportunity: opportunityTarget,
        lookup: lookupTarget,
        blDocument: blDocumentTarget,
      },
      setBookingRailMeta,
      registerBookingRailTarget,
      openBookingRailSection,
      closeBookingRailSection,
      toggleBookingRailSection,
    }),
    [
      kaiMounted,
      kaiVisible,
      kaiAnimating,
      notificationsMounted,
      notificationsVisible,
      notificationsAnimating,
      expandedPanel,
      kaiSectionOpen,
      notesSectionOpen,
      pageRailMeta,
      pageRailSectionOpen,
      pageRailTarget,
      pageRailHeaderActions,
      setPageRailHeaderActions,
      opportunityRailMeta,
      opportunitySectionOpen,
      lookupRailMeta,
      lookupSectionOpen,
      blDocumentRailMeta,
      blDocumentSectionOpen,
      bookingInsightSourceId,
      bookingInsightSectionOpen,
      bookingDocumentsSectionOpen,
      bookingBlGroupSourceId,
      bookingBlGroupSectionOpen,
      bookingAssignedRateRail,
      bookingAssignedRateSectionOpen,
      bookingMatchedScheduleRail,
      bookingMatchedScheduleSectionOpen,
      selectedRateLine,
      mobileRailOpen,
      mobileRailMode,
      desktopRailStack,
      toggleKaiPanel,
      closeKai,
      openMobileKai,
      toggleNotificationsPanel,
      closeNotifications,
      openMobileRail,
      toggleKaiSection,
      toggleNotesSection,
      collapseNotesSection,
      togglePageRailSection,
      openPageRailSection,
      closePageRailSection,
      toggleBookingInsightSection,
      toggleBookingDocumentsSection,
      setBookingBlGroupRail,
      toggleBookingBlGroupSection,
      setBookingAssignedRateRail,
      toggleBookingAssignedRateSection,
      setBookingMatchedScheduleRail,
      toggleBookingMatchedScheduleSection,
      applyBookingDocumentsAvailability,
      selectedBookingBlDocumentId,
      toggleBookingBlDocument,
      setBookingBlDocumentControls,
      setBookingInsightSourceId,
      setPageRailMeta,
      registerPageRailTarget,
      openRateLineDetail,
      closeRateLineDetail,
      opportunityTarget,
      lookupTarget,
      blDocumentTarget,
      setBookingRailMeta,
      registerBookingRailTarget,
      openBookingRailSection,
      closeBookingRailSection,
      toggleBookingRailSection,
      setOpportunityCompareFocused,
    ],
  );

  return (
    <RightRailContext.Provider value={value}>{children}</RightRailContext.Provider>
  );
}

export function useRightRail() {
  const ctx = useContext(RightRailContext);
  if (!ctx) {
    throw new Error("useRightRail must be used within RightRailProvider");
  }
  return ctx;
}
